import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { GroupChannelList } from '@sendbird/uikit-react/GroupChannelList';
import { GroupChannelListHeader } from '@sendbird/uikit-react/GroupChannelList/components/GroupChannelListHeader';

import { UnreadChannelFilter } from '@sendbird/chat/groupChannel';

import { Box, Button, Typography, useTheme, Avatar, Badge } from '@mui/material';

import styles from './chat.module.css';
import { useChat } from '../../contexts/ChatContext';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import { useSendBirdReady } from 'services/sendBird/sendBirdService';

const FILTER_ALL = 'all';
const FILTER_UNREAD = 'unread';

const ChannelsList = () => {
    const [selectedFilter, setSelectedFilter] = useState(FILTER_UNREAD);
    const { openWithChannel } = useChat();

    const ready = useSendBirdReady();

    if (!ready) return null;

    const sbCtx = useSendbirdStateContext();
    const sdk = sbCtx?.stores?.sdkStore?.sdk;

    if (!ready) return null;

    return (
        <>
            <GroupChannelList
                className={styles['unread-channels-list']}
                onChannelSelect={(channel) => openWithChannel(channel)}
                onChannelCreated={(channel) => console.log('Channel created:', channel)}
                channelListQueryParams={{
                    includeEmpty: true,
                    limit: 10,
                    unreadChannelFilter: selectedFilter === FILTER_UNREAD ? UnreadChannelFilter.UNREAD_MESSAGE : UnreadChannelFilter.ALL,
                }}
                renderHeader={() => (
                    <ChannelListHeader
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                )}
                renderChannelPreview={(props) => (
                    <ChannelPreview
                        {...props}
                        onClick={(channel) => openWithChannel(channel)}
                    />
                )}
            />
        </>
    );
};

const ChannelListHeader = ({ selectedFilter, setSelectedFilter }) => {
    const theme = useTheme();

    const filterButtonSx = {
        fontSize: '14px',
        fontWeight: 500,
        borderRadius: '8px',
        textTransform: 'capitalize',
        padding: '6px 16px',
        minWidth: 'unset',
        borderColor: theme.palette.grey[100],
        color: theme.palette.grey[300],
        '&:hover': {
            backgroundColor: theme.palette.blue[40],
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
        },
        '&.active': {
            backgroundColor: theme.palette.blue[40],
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
        },
        '::after': {
            display: 'none',
        },
    };

    return (
        <>
            <style>
                {`
                .sendbird-channel-header, .sendbird-channel-list__header {
                    margin: 0;
                    padding: 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-direction: row;
                    min-height: 97px;
                }
                .sendbird-channel-header {
                    min-width: 390px;
                }
                
                .sendbird-channel-list__header {
                    min-width: 375px;
                }
                `}
            </style>
            <GroupChannelListHeader
                renderMiddle={() => (
                    <>
                        <Typography
                            className='header-title'
                            sx={{
                                fontSize: '18px',
                                fontWeight: 700,
                                margin: '0 0 12px 24px',
                            }}
                        >Channels
                        </Typography>
                        <Box
                            sx={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
                            <Button
                                className={'header-button' + (selectedFilter === FILTER_ALL ? ' active' : '')}
                                onClick={() => {
                                    setSelectedFilter(FILTER_ALL);
                                }}
                                variant='outlined'
                                sx={filterButtonSx}
                            >
                                All
                            </Button>
                            <Button
                                className={'header-button' + (selectedFilter === FILTER_UNREAD ? ' active' : '')}
                                variant='outlined'
                                onClick={() => {
                                    setSelectedFilter(FILTER_UNREAD);
                                }}
                                sx={filterButtonSx}
                            >
                                Unread
                            </Button>
                        </Box>
                    </>
                )}
            />
        </>
    );
};

const ChannelPreview = ({ channel, tabIndex = 0, onClick }) => {
    const unread = channel.unreadMessageCount ?? 0;
    const lastMessageText = channel.lastMessage?.message || channel.lastMessage?.data || 'No messages yet';

    return (
        <Box
            role='button'
            tabIndex={tabIndex}
            onClick={() => onClick?.(channel)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                width: 379,
                height: 80,
                px: 2,
                py: 1,
                borderBottom: '1px solid #e0e0e0',
                cursor: 'pointer',
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
        >
            <Avatar
                sx={{
                    width: 48,
                    height: 48,
                    bgcolor: '#edf2f7',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: 16,
                    mr: 2,
                }}
            >
                {channel.name?.slice(0, 2).toUpperCase() || 'NA'}
            </Avatar>

            {/* Text area */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                    variant='subtitle1'
                    fontWeight={700}
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {channel.name || 'Unnamed Channel'}
                </Typography>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {lastMessageText}
                </Typography>
            </Box>

            {/* Unread badge */}
            {unread > 0 && (
                <Badge
                    badgeContent={unread}
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            fontSize: 14,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                        },
                    }}
                />
            )}
        </Box>
    );
};

ChannelsList.propTypes = {
    children: PropTypes.node,
};

ChannelListHeader.propTypes = {
    selectedFilter: PropTypes.string.isRequired,
    setSelectedFilter: PropTypes.func.isRequired,
};

ChannelPreview.propTypes = {
    channel: PropTypes.object.isRequired,
    tabIndex: PropTypes.number,
    onClick: PropTypes.func,
    renderChannelAction: PropTypes.func,
};

export default ChannelsList;